/**
 * dcent-coins.js — 지원 코인 브라우저 동작 (drop-03 레인 A).
 *
 * 원본: ~/work/homepage-design-src/src/components/CoinBrowser.tsx · CoinSearchModal.tsx.
 * Liquid 가 chrome 을 서버 렌더하고, 이 모듈이 코인 행과 코인 상세 모달을 그린다.
 *
 * 규율:
 *  - **innerHTML 0.** 행·모달은 createElement + textContent 로만 조립한다
 *    (phase8-coin-search.test.ts 가 세운 XSS 규율). 아이콘은 Liquid 가 렌더한 <template> 을 clone 한다.
 *  - **사용자 노출 문자열 0.** 카피는 전부 설정 island(`[data-dcent-coins-config]`)에서 온다.
 *    클라이언트 값이 들어가는 자리는 `%QUERY%` · `%SYMBOL%` 리터럴 토큰을 치환한다.
 *  - **중첩 ESC 는 모듈 단일 핸들러 + 레이어 스택**으로 푼다. 모달마다 capture 리스너를 달면
 *    같은 노드의 capture 는 등록 순서로 실행돼 먼저 연 검색 모달이 나중에 연 상세보다 먼저 닫힌다.
 *  - **스크롤 잠금은 W0 카운터(window.DcentScrollLock)만** 쓴다. 자체 저장/복원 금지.
 *  - 모듈 최상위 `return` 을 쓰지 않는다(ESM 에서 SyntaxError). 가드는 함수 안에 둔다.
 *
 * 양 샵 byte-identical — assets/dcent-* 는 parity 게이트 분모다.
 */

/**
 * 표·상세 모달의 기기 칩 노출 순서. **이 배열에 없는 기기는 칩이 아예 안 나온다** —
 * 자산의 wallets 에 값이 있어도 여기서 걸러진다(2026-08-27: x 를 빠뜨려 DCENT X 칩이
 * 필터에는 보이는데 표에는 안 나오던 회귀가 있었다. 기기를 늘릴 때 여기도 함께 늘린다).
 *
 * 순서는 마케팅 지정(2026-08-27): DCENT S → DCENT X → Biometric wallet → App.
 * 자산 데이터의 wallets 배열 순서와 무관하게 이 배열 순서로 렌더한다.
 */
const DEVICE_ORDER = ["card", "x", "bio", "app"];

/**
 * 상세 모달 "지원 디센트 제품" 카드에 세울 기기. 표의 칩(DEVICE_ORDER)과 **일부러 다르다** —
 * 여기는 살 수 있는 하드웨어를 보여주는 자리라 앱은 뺀다(2026-08-27 요청). 표의 칩·필터는
 * "이 자산을 어디서 쓸 수 있나" 를 답하는 자리라 App 이 그대로 남는다.
 * DEVICE_ORDER 에서 파생시키므로 순서·신규 기기는 한 곳만 고치면 따라온다.
 */
const DETAIL_DEVICE_ORDER = DEVICE_ORDER.filter((d) => d !== "app");

/* ── 모달 레이어 스택 — ESC 는 항상 최상단 레이어만 닫는다 ────────────────── */

const layers = [];

function pushLayer(closeFn) {
  layers.push(closeFn);
}

function popLayer(closeFn) {
  const i = layers.lastIndexOf(closeFn);
  if (i !== -1) layers.splice(i, 1);
}

if (typeof document !== "undefined" && !document.__dcentCoinsEsc) {
  document.__dcentCoinsEsc = true;
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || layers.length === 0) return;
    e.preventDefault();
    layers[layers.length - 1]();
  });
}

/* ── 순수 함수 (단위 테스트 진입점) ───────────────────────────────────────── */

/** 검색어(이름·심볼·네트워크·tags 부분일치, 소문자) AND 기기 필터. */
export function filterAssets(assets, query, device) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  return (assets || []).filter((a) => {
    if (device && !(a.wallets || []).includes(device)) return false;
    if (!q) return true;
    const hay = [a.name, a.symbol, a.network].concat(a.tags || []);
    return hay.some((s) =>
      String(s == null ? "" : s)
        .toLowerCase()
        .includes(q),
    );
  });
}

/* ── DOM 헬퍼 ─────────────────────────────────────────────────────────────── */

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function iconFrom(root, selector) {
  const tpl = root.querySelector(selector);
  return tpl ? tpl.content.cloneNode(true) : document.createDocumentFragment();
}

/** 검색어와 일치하는 구간만 브랜드색으로 감싼 fragment. innerHTML 미사용. */
function highlight(text, query) {
  const frag = document.createDocumentFragment();
  const source = String(text == null ? "" : text);
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) {
    frag.appendChild(document.createTextNode(source));
    return frag;
  }
  const lower = source.toLowerCase();
  let i = 0;
  let idx = lower.indexOf(q);
  while (idx !== -1) {
    if (idx > i)
      frag.appendChild(document.createTextNode(source.slice(i, idx)));
    const mark = el(
      "span",
      "dcent-coins__hl",
      source.slice(idx, idx + q.length),
    );
    frag.appendChild(mark);
    i = idx + q.length;
    idx = lower.indexOf(q, i);
  }
  frag.appendChild(document.createTextNode(source.slice(i)));
  return frag;
}

/**
 * Shopify `| t` 는 키가 `_html` 로 끝나지 않으면 값을 HTML escape 한다.
 * Liquid 가 그대로 출력하는 자리는 브라우저가 되돌려 주지만, 설정 island → textContent 경로는
 * 되돌릴 기회가 없어 `Network &amp; asset details` 처럼 엔티티가 그대로 보인다.
 * 그래서 JS 딕셔너리는 읽는 시점에 한 번 되돌린다. `&amp;` 를 마지막에 처리해야 이중 디코딩이 없다.
 * innerHTML 을 쓰지 않는 순수 문자열 치환이다.
 */
function decodeEntities(value) {
  if (typeof value === "string") {
    return value
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&");
  }
  if (value && typeof value === "object") {
    const out = Array.isArray(value) ? [] : {};
    Object.keys(value).forEach((k) => {
      out[k] = decodeEntities(value[k]);
    });
    return out;
  }
  return value;
}

function bindIconFallback(img, fallbackSrc) {
  const onError = () => {
    img.src = fallbackSrc;
    img.removeEventListener("error", onError);
  };
  img.addEventListener("error", onError);
}

/* ── 브라우저 인스턴스 ────────────────────────────────────────────────────── */

/** 브라우저 1개 초기화. 재호출 안전(멱등 가드). 모달은 첫 열림에 이 함수를 부른다. */
export function initCoinsBrowser(rootEl) {
  if (!rootEl || rootEl.__dcentCoinsBound) return;
  if (!window.DcentCoinAssets || !window.DcentScrollLock) return;
  rootEl.__dcentCoinsBound = true;

  const configEl = rootEl.querySelector("[data-dcent-coins-config]");
  if (!configEl) return;
  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (err) {
    return;
  }

  const t = decodeEntities(config.i18n || {});
  const pageSize = config.pageSize || 10;
  const input = rootEl.querySelector("[data-dcent-search-input]");
  const clearBtn = rootEl.querySelector("[data-dcent-search-clear]");
  const filterWrap = rootEl.querySelector("[data-coins-filter]");
  const filterToggle = rootEl.querySelector("[data-coins-filter-toggle]");
  const filterLabel = rootEl.querySelector("[data-coins-filter-label]");
  const filterMenu = filterWrap
    ? filterWrap.querySelector(".dcent-coins__filter-menu")
    : null;
  const headEl = rootEl.querySelector("[data-coins-head]");
  const rowsEl = rootEl.querySelector("[data-coins-rows]");
  const bannerEl = rootEl.querySelector("[data-coins-banner]");
  const emptyEl = rootEl.querySelector("[data-coins-empty]");
  const emptyTextEl = rootEl.querySelector("[data-coins-empty-text]");
  const emptyClearBtn = rootEl.querySelector("[data-coins-empty-clear]");
  const moreEl = rootEl.querySelector("[data-coins-more]");
  const moreBtn = moreEl
    ? moreEl.querySelector(".dcent-coins__more-btn")
    : null;
  const modalRoot = rootEl.querySelector("[data-coins-modal-root]");

  const skeletonEl = rootEl.querySelector("[data-coins-skeleton]");

  /* MAGE-635: 데이터 도착 전 첫 render()가 "결과 없음" 빈 상태를 먼저 노출하던 문제 —
     loading 동안은 empty 를 숨기고 서버 렌더 스켈레톤을 보여 준다. */
  const state = { assets: [], query: "", device: null, visible: pageSize, loading: true };

  /* 검색어 주입(퀵버튼·모달 초기화 공용) */
  function setQuery(next) {
    state.query = next;
    if (input) {
      input.value = next;
      input.dispatchEvent(new Event("input", { bubbles: true }));
    } else {
      state.visible = pageSize;
      render();
    }
  }

  function reset() {
    state.device = null;
    state.visible = pageSize;
    if (filterLabel && filterMenu) {
      const allOption = filterMenu.querySelector(
        '[data-coins-filter-option][data-device=""]',
      );
      if (allOption) selectDevice(allOption, false);
    }
    setQuery("");
  }

  rootEl.__dcentCoins = { setQuery, reset };

  /* ── 검색 ── */
  if (input) {
    input.addEventListener("input", () => {
      state.query = input.value;
      state.visible = pageSize;
      render();
    });
  }
  if (emptyClearBtn)
    emptyClearBtn.addEventListener("click", () => setQuery(""));

  /* ── 기기 드롭다운 ── */
  function closeFilter() {
    if (!filterMenu || !filterToggle) return;
    filterMenu.hidden = true;
    filterToggle.setAttribute("aria-expanded", "false");
  }

  function selectDevice(option, rerender) {
    const value = option.getAttribute("data-device") || "";
    state.device = value === "" ? null : value;
    state.visible = pageSize;
    if (filterMenu) {
      filterMenu.querySelectorAll("[data-coins-filter-option]").forEach((o) => {
        const on = o === option;
        o.classList.toggle("is-selected", on);
        o.setAttribute("aria-selected", on ? "true" : "false");
      });
    }
    if (filterLabel) {
      const labelSpan = option.querySelector("span");
      filterLabel.textContent = labelSpan ? labelSpan.textContent : "";
    }
    closeFilter();
    if (rerender !== false) render();
  }

  if (filterToggle && filterMenu) {
    filterToggle.addEventListener("click", () => {
      const open = filterMenu.hidden;
      filterMenu.hidden = !open;
      filterToggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
    filterMenu
      .querySelectorAll("[data-coins-filter-option]")
      .forEach((option) => {
        option.addEventListener("click", () => selectDevice(option, true));
      });
    document.addEventListener("mousedown", (e) => {
      if (filterMenu.hidden) return;
      if (filterWrap && filterWrap.contains(e.target)) return;
      closeFilter();
    });
  }

  /* ── 더 보기 ── */
  function showMore() {
    state.visible += pageSize;
    render();
  }

  if (moreBtn) {
    moreBtn.addEventListener("click", showMore);
  }

  /* 무한 스크롤 — 목록 끝(더 보기 자리)이 화면에 들어오면 다음 묶음을 자동으로 잇는다.
   * 지원 코인은 수천 건이라 버튼을 반복해 누르게 하지 않는다(MAGE-572).
   *
   * · 감시 대상은 버튼이 아니라 그 컨테이너다. 남은 행이 없으면 render() 가 `hidden` 을 걸어
   *   display:none 이 되고, 그때 IntersectionObserver 는 교차를 보고하지 않으므로 저절로 멈춘다.
   * · root 는 뷰포트가 아니라 **가장 가까운 스크롤 조상**이어야 한다 — 검색 모달(compact)은
   *   `.dcent-coins-modal__body` 안에서 스크롤되므로 root 를 null 로 두면 영영 교차하지 않는다.
   * · 한 번 교차에 한 묶음(pageSize)만 잇는다. 이어 붙인 행들이 센티널을 화면 밖으로 밀어내
   *   교차가 풀리고, 사용자가 다시 내려오면 그때 다음 묶음이 붙는다. 목록이 짧아 센티널이
   *   계속 보이면 남은 행이 다 붙을 때까지 이어지는데, 그건 화면이 이미 끝을 보여주고 있다는 뜻이다.
   * · IntersectionObserver 가 없는 환경에서는 버튼이 그대로 남아 종전처럼 동작한다(그래서 지우지 않고
   *   자동일 때만 감춘다). */
  if (moreEl && typeof IntersectionObserver === "function") {
    let scrollRoot = moreEl.parentElement;
    while (scrollRoot && scrollRoot !== document.body) {
      const oy = getComputedStyle(scrollRoot).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      scrollRoot = scrollRoot.parentElement;
    }
    if (scrollRoot === document.body) scrollRoot = null;

    moreEl.classList.add("is-auto");
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        showMore();
        // 다시 관찰해 교차 상태를 새로 평가한다. IntersectionObserver 는 상태가 **바뀔 때만**
        // 알리므로, 한 묶음을 이어도 센티널이 여전히 보이는 경우(화면이 크거나 남은 행이 적을 때)
        // 두 번째 통지가 오지 않아 목록이 그대로 멈춰 버린다. 이어 붙인 행이 센티널을 화면 밖으로
        // 밀어내면 그때 자연히 멈춘다.
        io.unobserve(moreEl);
        io.observe(moreEl);
      },
      { root: scrollRoot, rootMargin: "200px 0px" },
    );
    io.observe(moreEl);
  }

  /* ── 행 조립 ── */
  function buildRow(asset) {
    const row = el("div", "dcent-coins__row");
    row.setAttribute(
      "data-coin-key",
      String(asset.currencyId) + String(asset.network),
    );

    const assetCell = el("div", "dcent-coins__asset");
    const img = el("img", "dcent-coins__asset-img");
    img.src = asset.icon || config.iconFallback32;
    img.alt = "";
    img.loading = "lazy";
    img.draggable = false;
    img.width = 32;
    img.height = 32;
    bindIconFallback(img, config.iconFallback32);
    assetCell.appendChild(img);

    const text = el("div", "dcent-coins__asset-text");
    const line = el("div", "dcent-coins__asset-line");
    const nameSpan = el("span", "dcent-coins__asset-name");
    nameSpan.appendChild(highlight(asset.name, state.query));
    const symbolSpan = el("span", "dcent-coins__asset-symbol");
    symbolSpan.appendChild(highlight(asset.symbol, state.query));
    line.appendChild(nameSpan);
    line.appendChild(symbolSpan);
    const networkSpan = el("span", "dcent-coins__asset-network");
    networkSpan.appendChild(highlight(asset.network, state.query));
    text.appendChild(line);
    text.appendChild(networkSpan);
    assetCell.appendChild(text);

    const caret = el("span", "dcent-coins__row-caret");
    caret.appendChild(iconFrom(rootEl, "[data-coins-icon-caret]"));
    assetCell.appendChild(caret);
    row.appendChild(assetCell);

    const devices = el("div", "dcent-coins__devices");
    DEVICE_ORDER.filter((d) => (asset.wallets || []).includes(d)).forEach(
      (d) => {
        devices.appendChild(
          el("span", "dcent-coins__chip", (config.deviceChip || {})[d] || d),
        );
      },
    );
    row.appendChild(devices);

    const featuresText =
      asset.features == null ? config.featuresLabel : asset.features;
    row.appendChild(el("div", "dcent-coins__features", featuresText));

    const featuresPc = el("div", "dcent-coins__features-pc");
    const check = el("span", "dcent-coins__features-check");
    check.appendChild(iconFrom(rootEl, "[data-coins-icon-check]"));
    featuresPc.appendChild(check);
    featuresPc.appendChild(el("span", null, featuresText));
    const pcCaret = el("span", "dcent-coins__features-caret");
    pcCaret.appendChild(iconFrom(rootEl, "[data-coins-icon-caret]"));
    featuresPc.appendChild(pcCaret);
    row.appendChild(featuresPc);

    row.addEventListener("click", () => openDetail(asset));
    return row;
  }

  /* ── 렌더 ── */
  function render() {
    if (!rowsEl) return;
    const filtered = filterAssets(state.assets, state.query, state.device);
    const visible = filtered.slice(0, state.visible);

    if (bannerEl && bannerEl.parentNode)
      bannerEl.parentNode.removeChild(bannerEl);
    rowsEl.replaceChildren();

    visible.forEach((asset, i) => {
      const row = buildRow(asset);
      if (i === 2 && visible.length > 3) row.classList.add("is-preban");
      rowsEl.appendChild(row);
    });

    if (bannerEl) {
      if (visible.length > 3) {
        bannerEl.hidden = false;
        rowsEl.insertBefore(bannerEl, rowsEl.children[3] || null);
      } else {
        bannerEl.hidden = true;
        rowsEl.appendChild(bannerEl);
      }
    }

    if (skeletonEl) skeletonEl.hidden = !state.loading;
    if (headEl) headEl.hidden = state.loading ? false : visible.length === 0;
    if (moreEl) moreEl.hidden = state.visible >= filtered.length;

    if (emptyEl) {
      emptyEl.hidden = state.loading || visible.length !== 0;
      if (visible.length === 0 && emptyTextEl) {
        const q = state.query.trim();
        emptyTextEl.textContent = q
          ? String(t.noResultsFor || "").replace("%QUERY%", q)
          : t.noResults || "";
      }
    }
  }

  /* ── 코인 상세 모달 ── */
  function openDetail(asset) {
    if (!modalRoot) return;

    const overlay = el("div", "dcent-coins-detail");
    const dialog = el("div", "dcent-coins-detail__dialog");
    dialog.setAttribute("role", "dialog");
    dialog.setAttribute("aria-modal", "true");
    dialog.setAttribute("tabindex", "-1");
    dialog.addEventListener("click", (e) => e.stopPropagation());

    /* 헤더 */
    const header = el("div", "dcent-coins-detail__header");
    const lead = el("div", "dcent-coins-detail__lead");
    const img = el("img", "dcent-coins-detail__img");
    img.src = asset.icon || config.iconFallback48;
    img.alt = "";
    img.draggable = false;
    bindIconFallback(img, config.iconFallback48);
    lead.appendChild(img);
    const names = el("div", "dcent-coins-detail__names");
    const line = el("div", "dcent-coins-detail__line");
    line.appendChild(el("span", "dcent-coins-detail__name", asset.name));
    line.appendChild(el("span", "dcent-coins-detail__symbol", asset.symbol));
    names.appendChild(line);
    names.appendChild(el("span", "dcent-coins-detail__network", asset.network));
    lead.appendChild(names);
    header.appendChild(lead);

    const closeBtn = el("button", "dcent-coins-detail__close");
    closeBtn.type = "button";
    closeBtn.setAttribute("aria-label", t.closeAria || "");
    closeBtn.appendChild(iconFrom(rootEl, "[data-coins-icon-close]"));
    header.appendChild(closeBtn);
    dialog.appendChild(header);

    /* 본문 */
    const body = el("div", "dcent-coins-detail__body");

    const featuresText =
      asset.features == null ? config.featuresLabel : asset.features;
    const features = el("div", "dcent-coins-detail__features");
    String(featuresText)
      .split(" · ")
      .forEach((f) =>
        features.appendChild(el("span", "dcent-coins-detail__feature", f)),
      );
    body.appendChild(features);

    const infoGroup = el("div", "dcent-coins-detail__group");
    infoGroup.appendChild(
      el("p", "dcent-coins-detail__group-title", t.detailsTitle),
    );
    const infoRows = el("div", "dcent-coins-detail__rows");
    const tags = asset.tags || [];
    const address = window.DcentCoinAssets.assetAddress(asset);
    /* 시안의 6행은 값이 없어도 항상 전부 렌더한다. 없는 값은 "-"(더미 금지). */
    infoRows.appendChild(el("p", null, t.lNetwork + " : " + asset.network));
    /* Standard — 태그(토큰 규격)가 없으면: 네이티브 코인은 "Native Coin"(규격이 아니라 체인
       기본 코인이라는 실제 정보), 토큰인데 규격 미상이면 종전대로 "-" (2026-08-19). */
    let standardText = "-";
    if (tags.length > 0) standardText = tags.join(", ");
    else if (address == null) standardText = "Native Coin";
    infoRows.appendChild(el("p", null, t.lStandard + " : " + standardText));
    infoRows.appendChild(el("p", null, t.lListed + " : " + asset.name));
    /* Address — 네이티브 코인은 컨트랙트 주소가 존재하지 않는 개념이라 행을 그리지 않는다. */
    if (address != null) {
      infoRows.appendChild(
        el("p", "is-address", t.lAddress + " : " + address),
      );
    }
    /* 스왑 2행 — 네이티브 코인에만 낸다(토큰은 행 자체를 그리지 않는다 — 2026-08-19 결정,
       스왑 표시가 네이티브 한정이라 토큰에 "미지원" 행을 반복하는 건 소음이다).
       값은 자산 주석(dcent-coin-assets.js 의 스왑 SoT)이 있으면 실제 값, 없으면 "-". */
    if (address == null) {
      const swapProviders = asset.swapProviders;
      const hasSwap = swapProviders != null && swapProviders.length > 0;
      infoRows.appendChild(
        el(
          "p",
          null,
          t.lSwapSupport +
            " : " +
            (swapProviders == null ? "-" : hasSwap ? "Supported" : "Not supported"),
        ),
      );
      infoRows.appendChild(
        el(
          "p",
          null,
          t.lSwapProviders + " : " + (hasSwap ? swapProviders.join(", ") : "-"),
        ),
      );
    }
    infoGroup.appendChild(infoRows);
    body.appendChild(infoGroup);

    /* 제품 카드 — compact(검색 모달)에서는 미렌더 */
    if (!config.compact) {
      const productGroup = el("div", "dcent-coins-detail__group");
      productGroup.appendChild(
        el("p", "dcent-coins-detail__group-title", t.productsTitle),
      );
      const products = el("div", "dcent-coins-detail__products");
      DETAIL_DEVICE_ORDER.filter((d) => (asset.wallets || []).includes(d)).forEach(
        (d) => {
          const card = el("div", "dcent-coins-detail__product");
          const src = (config.productImage || {})[d];
          if (src) {
            const pImg = el("img", "dcent-coins-detail__product-img");
            pImg.src = src;
            pImg.alt = "";
            pImg.draggable = false;
            card.appendChild(pImg);
          } else {
            card.appendChild(el("span", "dcent-coins-detail__product-blank"));
          }
          const pText = el("div", "dcent-coins-detail__product-text");
          const pNames = el("div");
          pNames.appendChild(
            el(
              "span",
              "dcent-coins-detail__product-name",
              (config.deviceChip || {})[d] || d,
            ),
          );
          const tagWrap = el("span", "dcent-coins-detail__product-tags");
          String((t.productTags || {})[d] || "")
            .split(/\s+/)
            .filter(Boolean)
            .forEach((tag) => tagWrap.appendChild(el("span", null, tag)));
          pNames.appendChild(tagWrap);
          pText.appendChild(pNames);
          const href = (config.productUrl || {})[d];
          if (href) {
            const link = el("a", "dcent-coins-detail__product-cta", t.moreBtn);
            link.href = href;
            pText.appendChild(link);
          }
          card.appendChild(pText);
          products.appendChild(card);
        },
      );
      productGroup.appendChild(products);
      body.appendChild(productGroup);
    }

    /* 이미 디센트 보유 */
    const own = el("div", "dcent-coins-detail__own");
    const ownText = el("div", "dcent-coins-detail__own-text");
    ownText.appendChild(el("p", "dcent-coins-detail__own-title", t.ownTitle));
    ownText.appendChild(
      el(
        "p",
        "dcent-coins-detail__own-desc",
        String(t.ownDesc || "").replace("%SYMBOL%", asset.symbol),
      ),
    );
    own.appendChild(ownText);
    // URL 이 있으면 링크, 없으면 원본과 같은 장식 버튼(사용자 결정 2026-08-03).
    // 원본 CoinBrowser 의 이 버튼은 onClick 이 없어 외형만 존재한다.
    const ownUrl = (config.ctaUrl || {}).own;
    const ownLabel = String(t.ownCta || "").replace("%SYMBOL%", asset.symbol);
    if (ownUrl) {
      const ownCta = el("a", "dcent-coins-detail__own-cta", ownLabel);
      ownCta.href = ownUrl;
      own.appendChild(ownCta);
    } else {
      const ownCta = el("button", "dcent-coins-detail__own-cta", ownLabel);
      ownCta.type = "button";
      own.appendChild(ownCta);
    }
    body.appendChild(own);

    dialog.appendChild(body);
    overlay.appendChild(dialog);

    function close() {
      overlay.remove();
      window.DcentScrollLock.unlock();
      popLayer(close);
    }
    overlay.addEventListener("click", close);
    closeBtn.addEventListener("click", close);

    modalRoot.appendChild(overlay);
    window.DcentScrollLock.lock();
    pushLayer(close);
    dialog.focus();
  }

  /* ── 데이터 ── */
  render();
  window.DcentCoinAssets.fetchAssets({
    remoteUrl: config.remoteUrl,
    snapshotUrl: config.snapshotUrl,
  })
    .then((assets) => {
      state.assets = assets;
      state.loading = false;
      render();
    })
    .catch(() => {
      /* fetchAssets 는 폴백까지 실패해도 [] 를 resolve 하지만, 만약을 위해 스켈레톤이
         영원히 남지 않게 닫는다. */
      state.loading = false;
      render();
    });
}

/* ── 페이지 셸의 인기 자산 퀵버튼 ─────────────────────────────────────────── */

export function initCoinsQuickButtons(root = document) {
  root.querySelectorAll("[data-coins-quick]").forEach((btn) => {
    if (btn.__dcentCoinsBound) return;
    btn.__dcentCoinsBound = true;
    btn.addEventListener("click", () => {
      const section =
        btn.closest('[data-section-type="dcent-page-coins-browser"]') ||
        document;
      const browser = section.querySelector("[data-dcent-coins]");
      if (browser && browser.__dcentCoins)
        // data-query = 검색어 override — 심볼로는 네트워크 자산이 안 잡히는 버튼용(예: XLM→Stellar)
        browser.__dcentCoins.setQuery(
          btn.getAttribute("data-query") ||
            btn.getAttribute("data-symbol") ||
            "",
        );
    });
  });
}

/* ── 검색 모달 트리거 (공유 컴포넌트 CTA) ─────────────────────────────────── */

export function initCoinsModalTriggers(root = document) {
  root.querySelectorAll("[data-dcent-coins-modal-open]").forEach((trigger) => {
    if (trigger.__dcentCoinsBound) return;
    if (!window.DcentCoinAssets || !window.DcentScrollLock) return;
    trigger.__dcentCoinsBound = true;

    trigger.addEventListener("click", (e) => {
      const modal = document.getElementById(
        trigger.getAttribute("aria-controls") || "",
      );
      if (!modal) return;
      e.preventDefault();
      openSearchModal(modal, trigger);
    });
  });
}

function openSearchModal(modal, trigger) {
  if (modal.__dcentOpen) return;
  modal.__dcentOpen = true;
  modal.hidden = false;
  window.DcentScrollLock.lock();

  const browser = modal.querySelector("[data-dcent-coins]");
  if (browser) initCoinsBrowser(browser);

  const dialog = modal.querySelector("[data-dcent-coins-modal-dialog]");
  const closeBtn = modal.querySelector("[data-dcent-coins-modal-close]");

  function close() {
    if (!modal.__dcentOpen) return;
    modal.__dcentOpen = false;
    modal.hidden = true;
    window.DcentScrollLock.unlock();
    popLayer(close);
    /* 닫으면 검색어·필터를 초기화한다(원본 동작). */
    if (browser && browser.__dcentCoins) browser.__dcentCoins.reset();
    modal.removeEventListener("click", onOverlayClick);
    if (closeBtn) closeBtn.removeEventListener("click", close);
    if (trigger) trigger.focus();
  }

  function onOverlayClick(e) {
    if (dialog && dialog.contains(e.target)) return;
    close();
  }

  modal.addEventListener("click", onOverlayClick);
  if (closeBtn) closeBtn.addEventListener("click", close);
  pushLayer(close);

  /* 포커스는 다이얼로그로 — 검색 인풋에 넣으면 모바일 키보드가 바텀시트를 덮는다. */
  if (dialog) dialog.focus();
}

/* ── 자동 초기화 ──────────────────────────────────────────────────────────── */

function autoInit() {
  document
    .querySelectorAll("[data-dcent-coins]:not([data-coins-lazy])")
    .forEach(initCoinsBrowser);
  initCoinsQuickButtons();
  initCoinsModalTriggers();
}

if (typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", autoInit);
}
